import { Box, Flex, SimpleGrid } from '@chakra-ui/react';
import { isEmpty } from 'lodash';
import { useEffect, useState } from 'react';
import { FilterRemoveIcon } from '@assets/icons/index';
import ButtonOutline from '@components/buttons/buttonOutline';
import ButtonRegular from '@components/buttons/buttonRegular';
import FilterMultipleDate from '@components/filter/multipleDate';
import Select from '@components/filter/select';
import { TextBold, TextRegular } from '@components/typography';
import useGetStatus from '@domain/auditTrail/useGetStatus';
import useGetDC from '@domain/dc/useGetDC';
import { useGetGroupType } from '@domain/groupType/useGroupType';
import { useGetCustomerGroup } from '@domain/customerGroup/useCustomerGroup';
import useGetSelloutType from '@domain/selloutType/useGetSelloutType';
import checkFilterInformation from '@pages/AuditTrailSellOut/helpers/checkFilterInformation';
import { useFilter } from '@pages/AuditTrailSellOut/state/filters';
import { initialFilterState } from './const';
import '../styles.css';
import { useGetCustomerList } from '@domain/selloutMonitoring/useGetCustomerList';

const FilterList = ({ isOpen, onClose }) => {
    const filters = useFilter();
    const [tempFilter, setTempFilter] = useState(() => ({ ...initialFilterState, ...filters }));

    const dc = useGetDC({ enabled: !!tempFilter?.dcSearch, search: tempFilter?.dcSearch });
    const groupType = useGetGroupType({ enabled: true });
    const custGroup = useGetCustomerGroup({
        enabled: !!tempFilter?.custGroupSearch,
        search: tempFilter?.custGroupSearch,
    });
    const parahub = useGetCustomerList({
        enabled: !!tempFilter?.parahubSearch,
        search: { group: tempFilter?.custGroup?.name, customer: tempFilter?.parahubSearch },
    });
    const selloutType = useGetSelloutType({ enabled: true });
    const status = useGetStatus();

    useEffect(() => {
        setTempFilter((prev) => ({ ...prev, ...filters }));
    }, [filters]);

    const onResetFilter = () => {
        setTempFilter(initialFilterState);
        filters.onClearFilter();
        onClose();
    };

    const onApplyFilter = () => {
        filters.setFilter({
            ...tempFilter,
            page: 1,
        });
        onClose();
    };

    const onRemoveFilterItem = (key) => {
        const resetValue = Array.isArray(tempFilter[key])
            ? []
            : isEmpty(tempFilter[key])
              ? initialFilterState[key]
              : '';
        filters.setFilter({ ...filters, [key]: resetValue });
        setTempFilter((prev) => ({ ...prev, [key]: resetValue }));
    };

    const updateTempFilter = (key, value) => {
        setTempFilter((prev) => ({ ...prev, [key]: value }));
    };

    const activeFiltersConfig = [
        {
            key: 'dc',
            label: 'Distribution Center',
            visible: filters?.dc?.length > 0,
            text: `${filters?.dc?.length} Terpilih`,
        },
        {
            key: 'groupType',
            label: 'Group Type',
            visible: !isEmpty(filters?.groupType),
            text: `Terpilih`,
        },
        {
            key: 'custGroup',
            label: 'Customer Group',
            visible: filters?.custGroup?.length > 0,
            text: `${filters?.custGroup?.length} Terpilih`,
        },
        {
            key: 'parahub',
            label: 'Customer ID',
            visible: filters?.parahub?.length > 0,
            text: `${filters?.parahub?.length} Terpilih`,
        },
        { key: 'dateSellout', label: 'Tanggal Sellout', visible: !isEmpty(filters?.dateSellout), text: 'Terpilih' },
        { key: 'dateUpload', label: 'Tanggal Upload', visible: !isEmpty(filters?.dateUpload), text: 'Terpilih' },
        {
            key: 'selloutType',
            label: 'Tipe Sellout',
            visible: !isEmpty(filters?.selloutType),
            text: filters?.selloutType?.name,
        },
        {
            key: 'status',
            label: 'Status',
            visible: !!filters?.status?.length,
            text: `${filters?.status?.length} Terpilih`,
        },
    ];

    return (
        <Box className="filters__container">
            {isOpen && (
                <Flex className="box-filters fade-animation-bottom">
                    <SimpleGrid columns={{ base: 2, lg: 3, xl: 5 }} spacing={4} w="full">
                        <Select
                            selected={tempFilter?.dc}
                            title="Distribution Center"
                            placeholder="Pilih DC"
                            refetchData={dc?.refetch}
                            data={dc?.data}
                            isLoading={dc?.isLoading}
                            onSearch={(query) => updateTempFilter('dcSearch', query)}
                            onChangeFilter={(val) => updateTempFilter('dc', val)}
                            onClearOptions={() => updateTempFilter('dc', [])}
                        />

                        <Select
                            selected={tempFilter?.groupType}
                            title="Group Type"
                            placeholder="Pilih Group Type"
                            refetchData={groupType?.refetch}
                            data={groupType?.data}
                            isLoading={groupType?.isLoading}
                            isCanSearch={false}
                            onChangeFilter={(val) => updateTempFilter('groupType', [val])}
                            capitalizeOption
                            isMultiple={false}
                            onClearOptions={() => updateTempFilter('groupType', [])}
                        />

                        <Select
                            selected={tempFilter?.custGroup}
                            title="Customer Group"
                            placeholder="Pilih Customer Group"
                            refetchData={custGroup?.refetch}
                            data={custGroup?.data}
                            isLoading={custGroup?.isLoading}
                            onSearch={(query) => updateTempFilter('custGroupSearch', query)}
                            onChangeFilter={(val) => updateTempFilter('custGroup', val)}
                            onClearOptions={() => updateTempFilter('custGroup', [])}
                        />

                        <Select
                            selected={tempFilter?.parahub}
                            title="Customer ID"
                            placeholder="Pilih Customer ID"
                            refetchData={parahub?.refetch}
                            data={parahub?.data}
                            isLoading={parahub?.isLoading}
                            onSearch={(query) => updateTempFilter('parahubSearch', query)}
                            onChangeFilter={(val) => updateTempFilter('parahub', val)}
                            onClearOptions={() => updateTempFilter('parahub', [])}
                        />

                        <FilterMultipleDate
                            value={tempFilter?.dateSellout}
                            title="Tanggal Sellout"
                            placeholder="Pilih Tanggal Sellout"
                            onChange={([startDate, endDate]) => updateTempFilter('dateSellout', { startDate, endDate })}
                        />

                        <FilterMultipleDate
                            value={tempFilter?.dateUpload}
                            title="Tanggal Upload"
                            placeholder="Pilih Tanggal Upload"
                            onChange={([startDate, endDate]) => updateTempFilter('dateUpload', { startDate, endDate })}
                        />

                        <Select
                            selected={tempFilter?.selloutType}
                            title="Tipe Sellout"
                            placeholder="Pilih Tipe Sellout"
                            isMultiple={false}
                            refetchData={selloutType?.refetch}
                            data={selloutType?.data}
                            isLoading={selloutType?.isLoading}
                            isCanSearch={false}
                            onChangeFilter={(val) => updateTempFilter('selloutType', val)}
                            onClearOptions={() => updateTempFilter('selloutType', {})}
                        />

                        <Select
                            selected={tempFilter?.status[0]}
                            isMultiple={false}
                            title="Status"
                            placeholder="Pilih Status"
                            refetchData={status?.refetch}
                            data={status?.data}
                            isLoading={status?.isLoading}
                            isCanSearch={false}
                            onChangeFilter={(val) => updateTempFilter('status', [val])}
                            onClearOptions={() => updateTempFilter('status', [])}
                        />
                    </SimpleGrid>

                    <Flex className="filter-buttons__container">
                        <ButtonOutline
                            component="Reset All"
                            width="79px"
                            height="36px"
                            color="var(--color-primary)"
                            onClick={onResetFilter}
                        />
                        <ButtonRegular
                            component="Apply Filter"
                            width="98px"
                            height="36px"
                            color="var(--color-primary)"
                            onClick={onApplyFilter}
                        />
                    </Flex>
                </Flex>
            )}

            {!isOpen && checkFilterInformation(filters) && (
                <Flex className="filter-selected__container">
                    <TextBold>Filters by:</TextBold>

                    {activeFiltersConfig.map(
                        ({ key, label, visible, text }) =>
                            visible && (
                                <Flex key={key} className="filter-selected__item">
                                    <TextBold>{label}:</TextBold>
                                    <TextRegular className="selected-text">{text}</TextRegular>
                                    <FilterRemoveIcon onClick={() => onRemoveFilterItem(key)} />
                                </Flex>
                            ),
                    )}

                    <ButtonOutline
                        component="Reset All"
                        width="79px"
                        height="36px"
                        color="var(--color-primary)"
                        onClick={onResetFilter}
                    />
                </Flex>
            )}
        </Box>
    );
};

export default FilterList;
